import { AuthQueryParams, AuthQueryStates } from '@layers/web/constants/auth.ts'
import { loginWithActionEvent } from '@layers/web/constants/events.ts'
import { createAuthFeedback } from '@layers/web/utils/auth/feedback.ts'
import type { LocaleUrls } from '@layers/web/stores/types/locale.ts'
import type {
  AuthPagesMap,
  AuthPaths,
  LoginAction,
  LoginActionWithFeedback,
} from '@layers/web/utils/auth/types/auth.ts'

const AuthPages: AuthPagesMap = {
  default: 'default',

  logout: 'logout',
  login: 'login',
  signup: 'signup',
  whatsThis: 'whatsThis',
  forgot: 'forgot',

  myBookings: 'my-bookings',
  favourites: 'favourites',
  settings: 'settings',
}

const LOCALES_WITH_ENGLISH_AUTH_PATHS = ['en', 'pl', 'zh', 'zh-cn']

const loginWithAction = (name: string, payload: Record<string, any> = {}): LoginAction => ({
  name,
  ...payload,
})

const loginWhileTogglingFavourite = (
  tripTypeId?: string | number,
  message?: any
): LoginActionWithFeedback => ({
  action: loginWithAction(loginWithActionEvent.toggleFavourite, {
    tripTypeId,
  }),
  feedback: createAuthFeedback(message),
})

const swedishLoginPathWithState = (state: string): string => (
  `logga-in?${AuthQueryParams.STATE}=${state}`
)

const getAuthPaths = (locale: string): AuthPaths => LOCALES_WITH_ENGLISH_AUTH_PATHS.includes(locale) ? {
  [AuthPages.signup]: 'sign-up',
  [AuthPages.whatsThis]: 'whats-this',
  [AuthPages.login]: 'log-in',
  [AuthPages.logout]: 'log-out',
  [AuthPages.forgot]: 'forgot-password',
} : {
  [AuthPages.signup]: swedishLoginPathWithState(AuthQueryStates.SIGNUP),
  [AuthPages.whatsThis]: swedishLoginPathWithState(AuthQueryStates.WHATS_THIS),
  [AuthPages.login]: 'logga-in',
  [AuthPages.logout]: 'logga-ut',
  [AuthPages.forgot]: swedishLoginPathWithState(AuthQueryStates.FORGOT),
}

const authPathsUtil = (key: string, locale: string, urls: LocaleUrls): string => {
  const { logout, login, signup, forgot, whatsThis } = getAuthPaths(locale)

  switch (key) {
    case AuthPages.logout: {
      return urls['auth-callback'] + '?' + logout
    }
    case AuthPages.login: {
      return urls.auth + '/' + login
    }
    case AuthPages.signup: {
      return urls.auth + '/' + signup
    }
    case AuthPages.favourites: {
      return urls.auth + urls.authSlug.favourites
    }
    case AuthPages.whatsThis: {
      return urls.auth + '/' + whatsThis
    }
    case AuthPages.forgot: {
      return urls.auth + '/' + forgot
    }
    case AuthPages.settings: {
      return urls.auth + urls.authSlug.settings
    }
    case AuthPages.default:
    case AuthPages.myBookings:
    default: {
      return urls.auth + urls.authSlug['my-bookings']
    }
  }
}

const getGravatarByEmail = (email: string): string => {
  const hashedMail = md5(email)

  return `https://www.gravatar.com/avatar/${hashedMail}?d=mp`
}

/**
 * Facebook users signed in through cognito get the raw graph picture
 * JSON ({"data":{"url":...}}) as their picture attribute, while all
 * other providers store a plain url.
 */
const getPictureUrl = (picture?: string): string | null => {
  if (!picture) {
    return null
  }

  if (!picture.trim().startsWith('{')) {
    return picture
  }

  try {
    return JSON.parse(picture)?.data?.url || null
  } catch {
    return null
  }
}

export {
  loginWhileTogglingFavourite,
  AuthPages,
  getAuthPaths,
  authPathsUtil,
  getGravatarByEmail,
  getPictureUrl,
}
